import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import Modal from '@cp/src/components/Modal/Modal';
import { RootState } from '@cp/src/store';
import { useSelector } from 'react-redux';
import { strategyResult, triggerResult } from '../types';
import { IStrategy, ITrigger } from '../interfaces';
import ReactJson from 'react-json-view';
import { useDispatch } from 'react-redux';
import { getStrategiesByTenant, getTriggersByTenant } from '../paramountActions';
import useGetTenant from '@cp/pages/Paramount/hooks/useGetTenant';
import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { Typography } from '@navi/web-ui/lib/primitives';

const ViewStrategy = () => {
  const d3Container = useRef(null);
  const yAxisContainer = useRef(null);
  const dispatch = useDispatch();
  const tenant = useGetTenant();

  const [showModal, setShowModal] = React.useState(false);
  const [modalContent, setModalContent] = React.useState<IStrategy[]>([]);

  useEffect(() => {
    dispatch(getStrategiesByTenant(tenant));
    dispatch(getTriggersByTenant(tenant));
  }, []);

  const strategiesByTenant = useSelector((state: RootState) => state.paramountSlice.strategies);
  const triggersByTenant = useSelector((state: RootState) => state.paramountSlice.triggers);

  const params = readQueryParams();

  const timeline = params.timelineName;

  function roundTimeToNearestHour(timeStr: string) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const decimalMinutes = minutes / 60;
    const decimalTime = hours + decimalMinutes;
    return Number(decimalTime.toFixed(2));
  }

  const getStrategiesByTimeline = (timeline: string) => {
    return strategiesByTenant.filter(strategy => strategy.timelineName === timeline);
  };

  const processStrategies = (strategies: IStrategy[]): strategyResult[] => {
    const results: strategyResult[] = [];

    strategies.forEach(strategy => {
      const { id, initTimeList, dpadList, referenceId, name } = strategy;

      const sortedDpadList = [...dpadList].sort((a, b) => a - b);

      initTimeList.forEach(initTime => {
        const initTimeHours = roundTimeToNearestHour(initTime);

        if (sortedDpadList.length === 0) return;

        let start = sortedDpadList[0];
        let end = sortedDpadList[0];

        for (let i = 1; i < sortedDpadList.length; i++) {
          if (sortedDpadList[i] === end + 1) {
            end = sortedDpadList[i];
          } else {
            for (let bucket = start; bucket <= end; bucket++) {
              results.push({ id, startY: initTimeHours, endY: 20, bucket, referenceId, name });
            }
            start = sortedDpadList[i];
            end = sortedDpadList[i];
          }
        }

        for (let bucket = start; bucket <= end; bucket++) {
          results.push({ id, startY: initTimeHours, endY: 20, bucket, referenceId, name });
        }
      });
    });

    return results;
  };

  const getTriggersByTimeline = (timeline: string) => {
    return triggersByTenant.filter((trigger: ITrigger) => trigger.timelineName === timeline);
  };

  const processTriggers = (triggers: ITrigger[]): triggerResult[] => {
    const results: triggerResult[] = [];

    triggers.forEach(trigger => {
      const { dpadList, name, id, referenceId } = trigger;

      const numericDpadList = dpadList.map(Number);

      if (numericDpadList.length === 0) return;

      const sortedDpadList = [...numericDpadList].sort((a, b) => a - b);

      let start = sortedDpadList[0];
      let end = sortedDpadList[0];

      for (let i = 1; i < sortedDpadList.length; i++) {
        if (sortedDpadList[i] === end + 1) {
          end = sortedDpadList[i];
        } else {
          results.push({ startX: start, endX: end, name, id, referenceId });
          start = sortedDpadList[i];
          end = sortedDpadList[i];
        }
      }
      results.push({ startX: start, endX: end, name, id, referenceId });
    });
    return results;
  };

  const strategies = processStrategies(getStrategiesByTimeline(timeline));
  const triggers = [];

  let bucketMap: number[] = [];

  const getAdditionalBucketWidth = (bucket: number) => {
    if (bucketMap[bucket] === undefined) {
      bucketMap[bucket] = 0;
    }
    return bucketMap[bucket]++;
  };

  const handleOpenStrategyModal = (strategy: IStrategy) => {
    setShowModal(true);
    const filteredStrategy = getStrategiesByTimeline(timeline).filter(
      strat => strat.referenceId === strategy.referenceId
    );
    setModalContent(filteredStrategy);
  };
  const handleOpenTriggerModal = (trigger: ITrigger) => {
    setShowModal(true);
    const filteredTrigger = getTriggersByTimeline(timeline).filter(
      (trig: ITrigger) => trig.referenceId === trigger.referenceId
    );
    setModalContent(filteredTrigger);
  };

  const truncateName = (name: string, maxLength: number) => {
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  useEffect(() => {
    if (d3Container.current) {
      d3.select(d3Container.current).selectAll('*').remove();
      d3.select(yAxisContainer.current).selectAll('*').remove();

      const startegiesInThisBucket = new Map<number, number>();
      strategies.forEach(strategy => {
        if (!startegiesInThisBucket.has(strategy.bucket)) {
          startegiesInThisBucket.set(strategy.bucket, 0);
        }
        startegiesInThisBucket.set(
          strategy.bucket,
          startegiesInThisBucket.get(strategy.bucket) + 1
        );
      });

      // Calculate bucket widths based on the number of bars
      const bucketWidths: GenericObject = {};
      strategies.forEach(strategy => {
        if (!bucketWidths[strategy.bucket]) {
          bucketWidths[strategy.bucket] = 0;
        }
        bucketWidths[strategy.bucket] += 1;
      });

      const totalBuckets = Object.keys(bucketWidths).length;
      const totalBars = strategies.length;
      const maxBucket = Math.max(...strategies.map(strategy => strategy.bucket));

      // Dimensions and margins of the graph
      const margin = { top: 20, right: 30, bottom: 40, left: 40 };

      let totalGraphWidth = 0;

      // Generate custom x positions with increasing separations
      const customXPositions = [];
      let position = 0;

      for (let i = -8; i <= maxBucket + 5; i++) {
        //Change 180 to maxBucket
        const strategiesCount = startegiesInThisBucket.get(i); // i + 1 because bucket numbers start from 1
        const separation = strategiesCount ? 40 + strategiesCount * 30 : 20; // Increase the separation based on number of strategies or default to 40
        customXPositions.push(position);
        position += separation;
        totalGraphWidth += separation;
      }

      let minInitTime = 24,
        maxInitTime = 0;
      strategies.forEach(strategy => {
        if (minInitTime > strategy.startY) minInitTime = strategy.startY;
        if (maxInitTime < strategy.startY) maxInitTime = strategy.startY;
      });
      maxInitTime = Math.min(maxInitTime + 5, 25);
      const width = totalGraphWidth - 19;
      const height = 800 - margin.top - margin.bottom;
      const additionalHeight = 300; // Height for the below graph

      // Calculate the width for each bucket
      const bucketWidthScale = d3.scaleLinear().domain([0, totalBuckets]).range([0, width]);

      const bucketWidth: GenericObject = {};
      let currentX = 0;
      Object.keys(bucketWidths).forEach(bucket => {
        bucketWidth[bucket] = bucketWidthScale(bucketWidths[bucket]);
        currentX += bucketWidth[bucket];
      });

      // Create the SVG  for 1st graph container
      const svg = d3
        .select(d3Container.current)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom + additionalHeight + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const yAxisSvg = d3
        .select(yAxisContainer.current)
        .append('svg')
        .attr('width', margin.left)
        .attr('height', height + margin.top + margin.bottom + additionalHeight)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // X axis - buckets
      const x = d3
        .scaleOrdinal()
        .domain(d3.range(-8, customXPositions.length - 8))
        .range(customXPositions);

      svg
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${height})`)
        .call(
          d3.axisBottom(x).tickSize(-height) // Add tick marks extending into the graph area
        )
        .selectAll('.tick line')
        .attr('stroke-dasharray', '2,2') // Set stroke-dasharray for dotted lines
        .attr('stroke-opacity', '0.4'); // Optional: adjust opacity of gridlines

      // Y axis - hours of the day (0 to 23)
      const y = d3.scaleBand().domain(d3.range(minInitTime, maxInitTime)).range([height, 0]);

      svg
        .append('g')
        .attr('class', 'y-axis')
        .call(
          d3.axisLeft(y).tickSize(-width) // Add tick marks extending into the graph area
        )
        .selectAll('.tick line')
        .attr('stroke-dasharray', '2,2') // Set stroke-dasharray for dotted lines
        .attr('stroke-opacity', '0.4'); // Optional: adjust opacity of gridlines

      // Append y-axis label
      svg
        .append('text')
        .attr('class', 'y-axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('y', -margin.left + 20)
        .attr('x', -height / 2)
        .attr('text-anchor', 'middle');
      // .text('Hour of the Day');

      yAxisSvg
        .append('g')
        .attr('class', 'y-axis')
        .call(
          d3.axisLeft(y).tickSize(-width) // Add tick marks extending into the graph area
        )
        .selectAll('.tick line')
        .attr('stroke-dasharray', '2,2') // Set stroke-dasharray for dotted lines
        .attr('stroke-opacity', '0.4'); // Optional: adjust opacity of gridlines

      // Append y-axis label
      yAxisSvg
        .append('text')
        .attr('class', 'y-axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('y', -margin.left + 20)
        .attr('x', -height / 2)
        .attr('text-anchor', 'middle')
        .text('Hour of the Day');

      const colorScale = d3.scaleOrdinal(d3.schemePaired);

      const strategyGradients = {};

      // Create gradients for each strategy dynamically
      strategies.forEach(strategy => {
        if (!strategyGradients[strategy.referenceId]) {
          const color = colorScale(strategy.referenceId);
          // debugger;
          const gradient = svg
            .append('defs')
            .append('linearGradient')
            .attr('id', `gradient${strategy.referenceId}`)
            .attr('x1', '0%')
            .attr('y1', '0%')
            .attr('x2', '0%')
            .attr('y2', '100%');

          gradient
            .append('stop')
            .attr('offset', '0%')
            .attr('stop-color', d3.color(color).brighter(1));
          gradient.append('stop').attr('offset', '100%').attr('stop-color', color);

          strategyGradients[strategy.referenceId] = `url(#gradient${strategy.referenceId})`;
        }
      });

      const getYPosition = (startY: number) => {
        const roundedStartY = Math.floor(startY);
        const isDecimal = startY % 1 !== 0;
        return y(roundedStartY) + 15 - (isDecimal ? 10 : 0);
      };

      // Draw bars for each strategy in first graph
      strategies.forEach((strategy, indx) => {
        const barWidth = 20; // Adjust the bar width as needed

        const barX =
          x(strategy.bucket) + getAdditionalBucketWidth(strategy.bucket) * (barWidth + 12);

        // Append a circle element
        svg
          .append('circle')
          .attr('cx', barX + barWidth / 2)
          .attr('cy', getYPosition(strategy.startY))
          .attr('r', 10)
          .style('fill', `url(#gradient${strategy.referenceId})`)
          .attr('stroke', 'black')
          .attr('stroke-width', 1)
          .on('click', () => handleOpenStrategyModal(strategy))
          .on('mouseover', () => {});

        // Append the vertical text label
        svg
          .append('text')
          .attr(
            'transform',
            `rotate(-90, ${barX + barWidth / 2 + 5}, ${getYPosition(strategy.startY) - 20})`
          )
          .attr('x', barX + barWidth / 2 + 5)
          .attr('y', getYPosition(strategy.startY) - 20)
          .attr('font-size', '13px')
          //.attr('text-anchor', 'middle')
          .text(truncateName(strategy.name, 16));
      });

      bucketMap = [];
    }
  }, [d3Container.current, strategiesByTenant]);

  return (
    <div style={{ display: 'flex', width: '50%' }}>
      {strategies?.length > 0 ? (
        <div className="flex flex-col overflow-scroll justify-start h-[850px]">
          <div className="flex flex-row overflow-y-hidden overflow-scroll">
            <div
              ref={yAxisContainer}
              style={{
                backgroundColor: 'white',
                position: 'sticky',
                left: 0,
                zIndex: 1
                //borderRight: '1px solid #ddd' // Optional: to add a separator line
              }}
            />
            <div
              style={{ overflowX: 'scroll', overflowY: 'hidden', marginLeft: -40 }}
              ref={d3Container}
            />
          </div>
          <Typography variant="p1" className="flex justify-center text-base">
            DPAD
          </Typography>
        </div>
      ) : (
        <div className="w-[100%] flex justify-center items-center">No data available</div>
      )}
      <Modal
        iconButtonSize={'small'}
        headerClassName="bg-[#f1f9f9] rounded"
        contentClassName="w-[800px] h-[600px] overflow-auto mx-[20px] my-[20px]"
        visible={showModal}
        header={'Strategy'}
        onClose={() => setShowModal(false)}
      >
        <ReactJson src={modalContent[0]} name={modalContent[0]?.name} />
      </Modal>
    </div>
  );
};

export default ViewStrategy;
